import { MutableRefObject, useEffect } from 'react'
import { Campaign, DynamicVariables, EnvConfigShare } from '@interfaces'
import { Campaign as SharedCampaign } from '@sendspark/player/dist/types'
import { VideoPlayerEvents } from '@sendspark/player/dist/VideoPlayer'
import { ANALYTICS_TITLE } from '@constants/constants'
import { SegmentEvents } from '@constants/SegementEvents'
import { getDynamicCampaignVariables, getIp } from '@utils/index'
import { useRouter } from 'next/router'
import { useLocalStorage } from '@hooks'
import { useAhoyAnalyticsTracker, getAhoyAnalyticsTrackerConfig, useAnalytics } from '@sendspark/analytics'
import { useSegmentTracker } from './useSegmentTracker'
import { useVisitorId } from './useVisitorId'

interface UseAnalyticsTrackingProps {
  campaign: Campaign
  env: EnvConfigShare
  isEditPage: boolean
  isOwnerVideo: boolean
  isLinkedInRequest: boolean
  ignoreTracking: boolean
  campaignUpdate: any
  dynamicVariables: DynamicVariables
  prospectTrackVariables: DynamicVariables
  /**
   * The mounted player, so a CTA click can be recorded in Insight too (SK-567). The CTA lives
   * here in share, so without this the player — which owns the Insight session — never learns
   * the click happened and Insight reports zero conversions.
   */
  playerEventsRef?: MutableRefObject<VideoPlayerEvents | null>
}

export const useAnalyticsTracking = ({
  campaign,
  env,
  isEditPage,
  isOwnerVideo,
  isLinkedInRequest,
  ignoreTracking,
  campaignUpdate,
  dynamicVariables,
  prospectTrackVariables,
  playerEventsRef,
}: UseAnalyticsTrackingProps) => {
  const router = useRouter()
  const visitorId = useVisitorId()
  const [analitycsIdentityValue] = useLocalStorage('ahoyData', {})
  const [anonymousId] = useLocalStorage('ajs_anonymous_id', '')
  const campaignCreatorWorkspaceId = campaign.creator?.workspaceId

  const segmentTracker = useSegmentTracker(
    env.apiV2Url,
    campaignCreatorWorkspaceId,
    env.cryptoKey
  )

  const { ahoyAnalyticsTracker } = useAhoyAnalyticsTracker({
    config: {
      ...getAhoyAnalyticsTrackerConfig(env.beaconUrl),
      trackVisits: !isEditPage,
      headers: {
        [process.env.NEXT_PUBLIC_CUSTOM_USER_AGENT || 'X-Snd-Value']:
          process.env.NEXT_PUBLIC_CUSTOM_USER_AGENT_VALUE || 'Sendspark Share',
      },
    },
    debug: env.beaconDebug,
  })

  const { analytics } = useAnalytics({
    campaign: (campaign as unknown) as SharedCampaign,
    analyticsTracker: ahoyAnalyticsTracker,
  })

  const trackCTAClick = async () => {
    if (isEditPage || isOwnerVideo) return
    analytics?.track('$cta_click', {
      video: campaign.meta.video,
    })

    // Insight side (SK-567). Reported through the player so the click joins the same session
    // as `view`/`play` and inherits its asset attribution; the player keeps this event out of
    // its legacy forward, so the line above stays the only write to the legacy sink.
    playerEventsRef?.current?.trackCtaClick()

    await segmentTracker(ANALYTICS_TITLE.CampaignCtaClick, {
      creatorEmail: campaign.creator?.email,
      visitor: visitorId,
      campaignId: campaign.id,
      videoId: campaign.meta.video?.id,
      videoUrl: `${campaign.shareUrl}${campaign.shareId}`,
      videoName: campaign.meta.name,
      workspace: {
        id: campaign.creator?.workspaceId,
        name: campaign.creator?.workspaceName,
      },
      buttonText: campaign?.messages[0]?.text || 'Button Text',
      buttonUrl: campaign?.messages[0]?.link || '',
      dynamicVariables: prospectTrackVariables,
      creatorId: campaign.creator?._id,
    })

    await segmentTracker(ANALYTICS_TITLE.CtaCovensionGot, {
      creatorEmail: campaign.creator?.email,
      campaignId: campaign.id,
      viewerId:
        (analitycsIdentityValue as any)?.userId || anonymousId || visitorId,
      shareUrl: window.location.href,
      ctaText: campaign?.messages[0]?.text || '',
      ctaUrl: campaign?.messages[0]?.link || '',
      workspace: {
        id: campaign.creator?.workspaceId,
        name: campaign.creator?.workspaceName,
      },
      creatorId: campaign.creator?._id,
    })
  }

  const trackViralLink = async () => {
    if (isEditPage) return
    await segmentTracker(ANALYTICS_TITLE.ViralLinkClick, {
      workspaceName: campaign?.workspaceName,
      creator: campaign.creator?._id,
      creatorEmail: campaign.creator?.email,
      source: 'View Page',
      visitor: visitorId,
      workspace: {
        id: campaign.creator?.workspaceId,
        name: campaign.creator?.workspaceName,
      },
    })
  }

  useEffect(() => {
    const trackingView = async () => {
      const ip = await getIp()

      // block linkedin prefetch from triggering analytics
      if (isLinkedInRequest || env.excludedIpForAnalytics?.includes(ip)) return

      await segmentTracker(ANALYTICS_TITLE.CampaignView, {
        creatorEmail: campaign.creator.email,
        visitor: visitorId,
        campaignId: campaign.id,
        videoId: campaign.meta.video?.id,
        workspace: {
          id: campaign.creator.workspaceId,
          name: campaign.creator.workspaceName,
        },
        dynamicVariables: prospectTrackVariables,
        creatorId: campaign.creator._id,
      })

      await segmentTracker(ANALYTICS_TITLE.ViewGot, {
        viewerId:
          (analitycsIdentityValue as any)?.userId || anonymousId || visitorId,
        shareUrl: window.location.href,
        workspace: {
          id: campaign.creator.workspaceId,
          name: campaign.creator.workspaceName,
        },
        creatorId: campaign.creator._id,
      })
      
      await segmentTracker(ANALYTICS_TITLE.VideoViewReceived, {
        viewerId:
          (analitycsIdentityValue as any)?.userId || anonymousId || visitorId,
        shareUrl: window.location.href,
        workspace: {
          id: campaign.creator.workspaceId,
          name: campaign.creator.workspaceName
        },
        creatorId: campaign.creator._id
      })

      if (campaign?.featureFlips?.dynamicVariables) {
        const variables: string[] = getDynamicCampaignVariables(
          campaign.meta.heading1 + campaign.meta.heading2
        )
        if (!!variables.length) {
          await segmentTracker(SegmentEvents.dynamicCampaignViewed, {
            dynamicVariables: {
              query: { ...router.query, ...dynamicVariables },
              text: [...variables],
            },
            campaignId: campaign.id,
            creator: campaign.creator.email,
            url: window.location.href,
            workspace: {
              id: campaign.creator.workspaceId,
              name: campaign.creator.workspaceName,
            },
            creatorId: campaign.creator._id,
          })
        }
      }
      if (
        !isEditPage &&
        !campaignUpdate &&
        analytics &&
        !ignoreTracking &&
        !env.excludedIpForBeaconTracking?.includes(ip) &&
        !isOwnerVideo
      ) {
        if (analytics) {
          try {
            analytics?.track('$visit', { video: campaign?.meta?.video });
          } catch (error) {
            console.error('Error tracking visit', error);
          }
        }
      }
    }

    if (!isEditPage && !campaignUpdate && analytics && !ignoreTracking) {
      // eslint-disable-next-line no-void
      void trackingView()
    }
  }, [analytics])

  return {
    analytics,
    trackCTAClick,
    trackViralLink,
    ahoyAnalyticsTracker,
    segmentTracker
  }
} 